import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import { ApolloError } from "apollo-boost";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { IAppState } from "../../../../store";
import { IApplicableCoupon, IBooking } from "../../../../reducers/bookings/types";
import { APPLY_OFFER_CODE } from "../../../../graphql/bookings/applyOfferCodeMutation";
import { GET_APPLICABLE_COUPONS } from "../../../../graphql/bookings/getApplicableCouponsQuery";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { formatGraphQLErrorMessage, toCurrency } from "../../../utils";

export interface IProps {
  booking: IBooking;
  onBookingUpdated: (updated: Partial<IBooking>) => void;
}

const DiscountOffersSection: React.FC<IProps> = ({ booking, onBookingUpdated }) => {
  const { t } = useTranslation();
  const snackbar = useSnackBar();
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { currency, locale } = website.organisation;

  const [couponCode, setCouponCode] = useState<string>(booking.appliedOffers?.[0]?.code || "");
  const [manualCode, setManualCode] = useState<string>("");
  const [selectedOfferId, setSelectedOfferId] = useState<string>("");
  const [couponLoading, setCouponLoading] = useState<boolean>(false);
  // Manual discount and a Discounts & Offers coupon are mutually exclusive -
  // a booking that already has a staff-applied discount can't also take a
  // coupon, so skip fetching eligible coupons and show why instead.
  const hasManualDiscount = !!booking.discount?.amount;

  const { data, loading: couponsLoading } = useQuery(GET_APPLICABLE_COUPONS, {
    variables: { bookingId: booking.id },
    skip: !booking?.id || !!couponCode || hasManualDiscount,
    fetchPolicy: "network-only"
  });
  const eligibleCoupons: IApplicableCoupon[] = data?.consumerGetApplicableCoupons || [];

  const [applyOfferCodeMutation] = useMutation(APPLY_OFFER_CODE, {
    onCompleted: (result) => {
      setCouponLoading(false);
      if (result?.consumerApplyOfferCode) {
        onBookingUpdated({
          discount: result.consumerApplyOfferCode.discount,
          appliedOffers: result.consumerApplyOfferCode.appliedOffers,
          paymentDetails: {
            ...booking.paymentDetails,
            ...result.consumerApplyOfferCode.paymentDetails
          }
        } as Partial<IBooking>);
        setCouponCode(result.consumerApplyOfferCode.appliedOffers?.[0]?.code || "");
        if (!result.consumerApplyOfferCode.appliedOffers?.length) {
          setSelectedOfferId("");
          setManualCode("");
        }
      }
      snackbar({
        message: result?.consumerApplyOfferCode?.appliedOffers?.length
          ? t("root:couponApplied")
          : t("root:couponRemoved"),
        variant: SnackBarVariant.SUCCESS
      });
    },
    onError: (error: ApolloError) => {
      setCouponLoading(false);
      setSelectedOfferId("");
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  const applyCouponToBooking = (_couponCode: string) => {
    if (booking?.id) {
      setCouponLoading(true);
      applyOfferCodeMutation({
        variables: {
          bookingId: booking.id,
          offerCode: _couponCode || undefined
        }
      });
    }
  };

  return (
    <div>
      <Typography variant="h3" style={{ marginBottom: 10 }}>
        {t("root:discountAndOffers")}
      </Typography>
      {couponCode ? (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body1" style={{ color: "#2e7d32" }}>
            {t("root:couponAppliedLabel")}: {couponCode}
            {!!booking.appliedOffers?.length && (
              <>
                {" "}
                (-
                {toCurrency(
                  booking.appliedOffers.reduce((sum, appliedOffer) => sum + appliedOffer.discountAmount, 0),
                  currency,
                  locale
                )}
                )
              </>
            )}
          </Typography>
          <Tooltip title={t("root:removeCoupon")} style={{ padding: 0 }}>
            <IconButton
              aria-label="remove coupon code"
              size="small"
              disabled={couponLoading}
              onClick={() => applyCouponToBooking("")}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ) : hasManualDiscount ? (
        <Typography variant="body2" color="error">
          {t("root:couponBlockedByDiscount")}
        </Typography>
      ) : (
        <Grid container spacing={2} alignItems="flex-end">
          {!!eligibleCoupons.length && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="applicable-coupon-label">{t("root:availableCoupons")}</InputLabel>
                <Select
                  labelId="applicable-coupon-label"
                  id="applicable-coupon"
                  value={selectedOfferId}
                  disabled={couponLoading}
                  onChange={(e) => {
                    const offerId = e.target.value as string;
                    setSelectedOfferId(offerId);
                    const coupon = eligibleCoupons.find((c) => c.offerId === offerId);
                    if (coupon) {
                      applyCouponToBooking(coupon.code);
                    }
                  }}
                  fullWidth
                >
                  {eligibleCoupons.map((coupon) => (
                    <MenuItem key={coupon.offerId} value={coupon.offerId}>
                      {coupon.name} ({coupon.code}) &mdash;{" "}
                      {coupon.discountValueType === "PERCENTAGE"
                        ? `${coupon.discountValue}%`
                        : toCurrency(coupon.discountValue * 100, currency, locale)}{" "}
                      {t("root:off")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {!couponsLoading && !eligibleCoupons.length && (
            <Grid item xs={12}>
              <Typography variant="body2" style={{ color: "#888" }}>
                {t("root:noCouponsAvailable")}
              </Typography>
            </Grid>
          )}
          <Grid item xs={8} sm={4}>
            <TextField
              label={t("root:couponCode")}
              id="coupon-code"
              fullWidth
              placeholder={t("root:enterCouponCode")}
              disabled={couponLoading}
              value={manualCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualCode(e.target.value)}
            />
          </Grid>
          <Grid item xs={4} sm={2}>
            <Button
              variant="contained"
              color="primary"
              disabled={couponLoading || !manualCode.trim()}
              onClick={() => applyCouponToBooking(manualCode.trim())}
            >
              {couponLoading ? <CircularProgress size={14} style={{ color: "#fff" }} /> : t("root:applyCoupon")}
            </Button>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default DiscountOffersSection;
